// ============================================================================
// CheckinDetailsReportScreen — operational reports (July 2026)
// ============================================================================
// Registration register: one row per checked-in room over a check-in date
// range — GRC number, guest identity, ID document, gender split, room, plan,
// tariff, and who processed the check-in. Modelled on the legacy PMS
// "Check in Details" export but rebuilt to CRS UI standards.
//
// Privacy: ID document numbers are MASKED in the web grid and in print; the
// Excel export carries them in full (both sit behind the report's RBAC).

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import DateRangePresetSelector from 'components/common/DateRangePresetSelector';
import { useDateRangePreset } from 'hooks/useDateRangePreset';
import { useReportList, useReportSummary } from 'hooks/api/useReport';
import { reportService } from 'api/services/reportService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { exportToExcel } from 'utils/exportToExcel';
import { printReport } from 'utils/printReport';
import { formatCurrency } from 'utils/currencyFormat';
import type {
  ReportListRequest,
  ReportFilter,
  CheckinDetailsRow,
  CheckinDetailsSummary,
} from 'api/types';
import 'components/modules/dashboard-module/dashboard.css';

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

const formatDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : '-';

/** ID privacy mask: keep the last 4 characters, hide the rest. */
const maskIdNumber = (id?: string | null): string => {
  if (!id) return '-';
  const tail = id.length > 4 ? id.slice(-4) : id.slice(-Math.max(1, id.length - 1));
  return `••••${tail}`;
};

interface CheckinDetailsReportScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','export'}. Fail-closed: [] renders no export/print. */
  allowedActions?: string[];
}

const CheckinDetailsReportScreen: React.FC<CheckinDetailsReportScreenProps> = ({
  allowedActions = [],
}) => {
  // ── Filter state ──────────────────────────────────────────────────────────
  const [propertyId, setPropertyId] = useState<string>('');
  const dateRange = useDateRangePreset('TODAY');
  const [filterOpen, setFilterOpen] = useState(false);

  const propertyLabelsRef = useRef<Record<string, string>>({});

  // ── Pagination ────────────────────────────────────────────────────────────
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const enabled = !!propertyId;

  React.useEffect(() => { setPage(1); }, [propertyId, dateRange.value.from, dateRange.value.to]);

  const listRequest: ReportListRequest = useMemo(() => ({
    propertyId,
    from: dateRange.value.from,
    to:   dateRange.value.to,
    page,
    pageSize,
  }), [propertyId, dateRange.value.from, dateRange.value.to, page, pageSize]);

  const summaryFilter: ReportFilter = useMemo(() => ({
    propertyId,
    from: dateRange.value.from,
    to:   dateRange.value.to,
  }), [propertyId, dateRange.value.from, dateRange.value.to]);

  const { data: rawData, pagination, loading, error, refetch } = useReportList<CheckinDetailsRow>(
    (req) => reportService.getCheckinDetails(req),
    listRequest,
    enabled,
  );
  const data = useMemo(
    () => rawData.map((r, i) => ({ ...r, id: `${r.bookingId}-${r.roomNumber}-${i}` })),
    [rawData],
  );
  const { data: summary, refetch: refetchSummary } = useReportSummary<CheckinDetailsSummary>(
    (f) => reportService.getCheckinDetailsSummary(f as ReportFilter),
    summaryFilter,
    enabled,
  );

  const appliedFilterCount = useMemo(() => {
    let n = 0;
    if (propertyId) n++;
    if (dateRange.value.preset !== 'TODAY') n++;
    return n;
  }, [propertyId, dateRange.value.preset]);

  const handlePageChange     = useCallback((idx: number) => setPage(idx + 1), []);
  const handlePageSizeChange = useCallback((size: number) => { setPageSize(size); setPage(1); }, []);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const handleClearFilters = useCallback(() => {
    dateRange.setPreset('TODAY');
    // Property is intentionally NOT reset — every report needs a property scope.
  }, [dateRange]);

  // ── Shared row fetch for export/print (all rows, capped at 10k) ───────────
  const fetchAllRows = useCallback(async (): Promise<CheckinDetailsRow[]> => {
    const all = await reportService.getCheckinDetails({ ...summaryFilter, page: 1, pageSize: 10000 });
    return all.data ?? [];
  }, [summaryFilter]);

  const periodLabel = dateRange.value.from === dateRange.value.to
    ? formatDate(dateRange.value.from)
    : `${formatDate(dateRange.value.from)} – ${formatDate(dateRange.value.to)}`;

  // ── Excel export (FULL ID numbers — behind 'export' RBAC) ─────────────────
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    if (!enabled) {
      notificationService.toast.warning('Select a property first.');
      return;
    }
    setExporting(true);
    try {
      const rows = await fetchAllRows();
      if (!rows.length) {
        notificationService.toast.info('No check-ins in the selected window.');
        return;
      }
      exportToExcel({
        data: rows.map((r) => ({
          GRCNo:          r.grcNumber ?? '',
          BookingNumber:  r.bookingNumber,
          Guest:          r.guestFullName ?? '',
          Nationality:    r.nationality ?? '',
          IdType:         r.idType ?? '',
          IdNumber:       r.idNumber ?? '',       // full — export is RBAC-gated
          Phone:          r.guestPhone ?? '',
          Pax:            r.pax,
          Male:           r.numMale,
          Female:         r.numFemale,
          Room:           r.roomNumber,
          Category:       r.roomCategoryName ?? '',
          Source:         r.bookingSource ?? '',
          Party:          r.partyName ?? '',
          CheckIn:        formatDateTime(r.checkInAt),
          ExpectedCheckOut: formatDate(r.expectedCheckOut),
          Plan:           r.mealPlanCode ?? '',
          Tariff:         Number(r.ratePerRoom ?? 0),
          Amount:         Number(r.roomAmount ?? 0),
          CheckedInBy:    r.checkedInByName ?? '',
        })),
        fileName: `checkin-details_${dateRange.value.from}_${dateRange.value.to}`,
      });
      notificationService.toast.success(`Exported ${rows.length} check-in rooms.`);
    } catch {
      notificationService.toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [enabled, fetchAllRows, dateRange.value.from, dateRange.value.to]);

  // ── Print (masked IDs, management-friendly) ───────────────────────────────
  const [printing, setPrinting] = useState(false);
  const handlePrint = useCallback(async () => {
    if (!enabled) {
      notificationService.toast.warning('Select a property first.');
      return;
    }
    setPrinting(true);
    try {
      const rows = await fetchAllRows();
      const s = summary;
      printReport({
        title: 'Check-in Details',
        propertyName: propertyLabelsRef.current[propertyId] ?? '',
        periodLabel,
        kpis: s ? [
          { label: 'Check-ins', value: String(s.totalCheckIns) },
          { label: 'Rooms', value: String(s.totalRooms) },
          { label: 'Pax', value: String(s.totalPax) },
          { label: 'Male / Female', value: `${s.maleCount} / ${s.femaleCount}` },
          { label: 'Amount', value: formatCurrency(s.totalAmount) },
        ] : [],
        columns: [
          { header: 'GRC No' }, { header: 'Guest' }, { header: 'Nationality' },
          { header: 'ID' }, { header: 'Pax', align: 'right' },
          { header: 'M/F', align: 'right' }, { header: 'Room' },
          { header: 'Category' }, { header: 'Source / Party' },
          { header: 'Check-In' }, { header: 'Exp. C/O' }, { header: 'Plan' },
          { header: 'Tariff', align: 'right' }, { header: 'Amount', align: 'right' },
          { header: 'By' },
        ],
        rows: rows.map((r) => [
          r.grcNumber ?? '',
          r.guestFullName ?? '',
          r.nationality ?? '',
          r.idType ? `${r.idType} ${maskIdNumber(r.idNumber)}` : '',
          String(r.pax),
          `${r.numMale}/${r.numFemale}`,
          r.roomNumber,
          r.roomCategoryName ?? '',
          [r.bookingSource, r.partyName].filter(Boolean).join(' — '),
          formatDateTime(r.checkInAt),
          formatDate(r.expectedCheckOut),
          r.mealPlanCode ?? '',
          formatCurrency(r.ratePerRoom),
          formatCurrency(r.roomAmount),
          r.checkedInByName ?? '',
        ]),
        footerNote: 'ID document numbers are masked for privacy. Full numbers are available in the Excel export (access-controlled).',
      });
    } catch {
      notificationService.toast.error('Print failed. Please try again.');
    } finally {
      setPrinting(false);
    }
  }, [enabled, fetchAllRows, summary, propertyId, periodLabel]);

  // ── Columns (14 → frozen first column for horizontal scroll comfort) ──────
  type Row = CheckinDetailsRow & { id: string };
  const num = (v: number) => (
    <span style={{ display: 'block', textAlign: 'right' }}>{formatCurrency(v)}</span>
  );
  const columns: SyutGridColumn<Row>[] = useMemo(() => [
    { id: 'grcNumber', header: 'GRC No', accessorKey: 'grcNumber', size: 140, enableSorting: true,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'guestFullName', header: 'Guest', accessorKey: 'guestFullName', size: 160, enableSorting: true,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'nationality', header: 'Nationality', accessorKey: 'nationality', size: 100,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'idNumber', header: 'ID Document', accessorKey: 'idNumber', size: 150,
      cell: ({ row }) => row.original.idType
        ? <span>{row.original.idType} <span style={{ color: 'var(--text-muted)' }}>{maskIdNumber(row.original.idNumber)}</span></span>
        : '-' },
    { id: 'guestPhone', header: 'Phone', accessorKey: 'guestPhone', size: 110,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'pax', header: 'Pax', accessorKey: 'pax', size: 55 },
    { id: 'gender', header: 'M / F', accessorKey: 'numMale', size: 65,
      cell: ({ row }) => `${row.original.numMale} / ${row.original.numFemale}` },
    { id: 'roomNumber', header: 'Room', accessorKey: 'roomNumber', size: 75, enableSorting: true },
    { id: 'roomCategoryName', header: 'Category', accessorKey: 'roomCategoryName', size: 130,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'sourceParty', header: 'Source / Party', accessorKey: 'partyName', size: 170,
      cell: ({ row }) => (
        <span>
          <span style={{ color: 'var(--text-muted)' }}>{row.original.bookingSource ?? ''}</span>
          {row.original.partyName ? ` — ${row.original.partyName}` : ''}
        </span>
      ),
    },
    { id: 'checkInAt', header: 'Check-In', accessorKey: 'checkInAt', size: 130, enableSorting: true,
      cell: ({ getValue }) => formatDateTime(getValue() as string) },
    { id: 'expectedCheckOut', header: 'Exp. C/O', accessorKey: 'expectedCheckOut', size: 105,
      cell: ({ getValue }) => formatDate(getValue() as string) },
    { id: 'mealPlanCode', header: 'Plan', accessorKey: 'mealPlanCode', size: 65,
      cell: ({ getValue }) => (getValue() as string) || '-' },
    { id: 'ratePerRoom', header: 'Tariff', accessorKey: 'ratePerRoom', size: 100, enableSorting: true,
      cell: ({ getValue }) => num(getValue() as number) },
    { id: 'roomAmount', header: 'Amount', accessorKey: 'roomAmount', size: 110, enableSorting: true,
      cell: ({ getValue }) => num(getValue() as number) },
    { id: 'checkedInByName', header: 'Checked-in By', accessorKey: 'checkedInByName', size: 140,
      cell: ({ getValue }) => (getValue() as string) || '-' },
  ], []);

  // ── ModuleHeader actions ──────────────────────────────────────────────────
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={16} />,
        label: 'Refresh',
        onClick: () => { refetch(); refetchSummary(); },
        variant: 'icon',
      },
    ];
    if (allowedActions.includes('export')) {
      buttons.push({
        id: 'export',
        icon: <IconSystem.download size={16} />,
        label: exporting ? 'Exporting…' : 'Export',
        tooltip: 'Export to Excel (full ID numbers)',
        onClick: handleExport,
        variant: 'icon',
      });
      buttons.push({
        id: 'print',
        icon: <IconSystem.print size={16} />,
        label: printing ? 'Preparing…' : 'Print',
        tooltip: 'Print check-in register (IDs masked)',
        onClick: handlePrint,
        variant: 'icon',
      });
    }
    return buttons;
  }, [refetch, refetchSummary, handleExport, handlePrint, exporting, printing, allowedActions]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Check-in Details"
        description="Registration register for a check-in date range — GRC numbers, guest identity, gender mix, rooms, and tariffs."
        icon={<IconSystem.users size={18} />}
        actions={actionButtons}
        filterConfig={{
          onClick: () => setFilterOpen((v) => !v),
          isActive: filterOpen,
          appliedCount: appliedFilterCount,
        }}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {enabled && summary && (
              <div style={{ padding: '0 1rem 0.5rem' }}>
                <div className="kpi-strip">
                  <div className="kpi-tile"><div className="kpi-tile-label">Check-ins</div><div className="kpi-tile-value">{summary.totalCheckIns}</div></div>
                  <div className="kpi-tile"><div className="kpi-tile-label">Rooms</div><div className="kpi-tile-value">{summary.totalRooms}</div></div>
                  <div className="kpi-tile"><div className="kpi-tile-label">Pax</div><div className="kpi-tile-value">{summary.totalPax}</div></div>
                  <div className="kpi-tile"><div className="kpi-tile-label">Male / Female</div><div className="kpi-tile-value">{summary.maleCount} / {summary.femaleCount}</div></div>
                  <div className="kpi-tile"><div className="kpi-tile-label">Amount</div><div className="kpi-tile-value">{formatCurrency(summary.totalAmount)}</div></div>
                </div>
              </div>
            )}

            <div className="data-grid-container">
              {!enabled ? (
                <div className="coming-soon-banner" style={{ margin: '0 1rem 1rem' }}>
                  <div className="coming-soon-banner-title">Pick a property in the filter panel</div>
                  <div className="coming-soon-banner-meta">Open the filters (top-right) and select a property to load check-ins.</div>
                </div>
              ) : error ? (
                <div className="coming-soon-banner" style={{ margin: '0 1rem 1rem' }}>
                  <div className="coming-soon-banner-title" style={{ color: '#ef4444' }}>Failed to load</div>
                  <div className="coming-soon-banner-meta">{error.message}</div>
                </div>
              ) : (
                <SyutGrid
                  data={data}
                  columns={columns}
                  enablePagination
                  manualPagination
                  totalCount={pagination?.totalItems ?? 0}
                  currentPage={page - 1}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  enableSorting
                  frozenLeftColumns={['grcNumber']}
                  emptyMessage={loading ? 'Loading…' : 'No check-ins in the selected window.'}
                />
              )}
            </div>
          </div>

          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label={<>Property <span className="text-danger">*</span></>}
                  value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')}
                  isAsync
                  loadOptions={loadProperties}
                  defaultOptions={true}
                  cacheOptions={false}
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Check-in Date</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <DateRangePresetSelector
                    preset={dateRange.value.preset}
                    from={dateRange.value.from}
                    to={dateRange.value.to}
                    onPresetChange={dateRange.setPreset}
                    onFromChange={dateRange.setCustomFrom}
                    onToChange={dateRange.setCustomTo}
                  />
                </div>
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckinDetailsReportScreen;
